import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Users, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { PresenceBadge } from '@/components/social/PresenceBadge';
import { UserAvatar } from '@/components/social/UserAvatar';
import { useGetOnlineFriendsFeedQuery, useListFriendsQuery } from '@/services/socialApi';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';

/** Oltre questa taglia l'elenco porta una ricerca (I-253: «facilmente» = ricerca se sono tanti). */
const SOGLIA_RICERCA = 10;

export interface AmicoScelto {
  /** Il `sub` QSocials dell'amico: la chiave della sfida di piattaforma (`target: {sub}`). */
  userId: string;
  nome: string;
}

interface SceltaAmicoSheetProps {
  onScegli: (amico: AmicoScelto) => void;
  /** Il ripiego, dove c'e': chi preferisce il codice trova lo stesso flusso di sempre (InviteSheet). Senza, il bottone non si rende. */
  onPreferisciCodice?: () => void;
  onClose: () => void;
}

/**
 * Il selettore «facilmente» di I-253 (T1-6): dalla lobby backgammon, l'elenco dei propri amici —
 * online in cima, ricerca per nome se sono piu' di una decina, un tocco manda alla sfida. Non
 * duplica la presenza riga per riga: «online» lo dice lo stesso feed della spunta di
 * `Friends.tsx` (`useGetOnlineFriendsFeedQuery`), una sola chiamata per l'intero elenco.
 *
 * Questo foglio SCEGLIE soltanto: chi chiama (`BackgammonLobby`) monta poi il pannello della sfida
 * di piattaforma (`SfidaSheetDaCatalogo`) per l'amico scelto — nessuna sfida parte da qui.
 *
 * ⚠️ Sta in `components/backgammon/` e legge il namespace `backgammon`, ma le sue nove chiavi sono generiche
 * («Scegli un amico», «Cerca amici per nome», …) e lo usano anche le Sfide di Clash Royale, senza il ripiego del
 * codice (`onPreferisciCodice` assente): una frase che nomina il backgammon qui la vedono anche loro. Spostarlo
 * e' un'altra PR (reference delle Sezioni dell'Hub, 26/10, §11, Q17).
 */
export function SceltaAmicoSheet({ onScegli, onPreferisciCodice, onClose }: SceltaAmicoSheetProps) {
  const { t } = useTranslation('backgammon');
  const lingua = useLinguaFormato();
  const haptics = useHaptics();
  const [query, setQuery] = useState('');
  const titoloId = useId();
  useLivelloIndietro(true, onClose);

  const friends = useListFriendsQuery();
  const online = useGetOnlineFriendsFeedQuery();
  const onlineIds = useMemo(
    () => new Set((online.data?.feed ?? []).map((f) => f.userId)),
    [online.data],
  );

  const totale = friends.data?.length ?? 0;
  const mostraRicerca = totale > SOGLIA_RICERCA;

  // Il nome di ripiego si calcola UNA volta e serve a sort/ricerca/riga — un amico senza
  // displayName si trova cercando anche il nome generico che vede a schermo, non solo il vero.
  const elenco = useMemo(() => {
    const lista = friends.data ?? [];
    const conNome = lista.map((f) => ({
      amico: f,
      nome: f.displayName || t('invite.pickFriendUnknown'),
    }));
    // Online prima, poi per nome — cosi' l'ordine non salta a ogni rilettura del feed online.
    const ordinati = conNome.sort((a, b) => {
      const aOn = onlineIds.has(a.amico.friendUserId) ? 0 : 1;
      const bOn = onlineIds.has(b.amico.friendUserId) ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return a.nome.localeCompare(b.nome, lingua);
    });
    const q = query.trim().toLowerCase();
    if (!q) return ordinati;
    return ordinati.filter((r) => r.nome.toLowerCase().includes(q));
  }, [friends.data, onlineIds, query, t, lingua]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titoloId}
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative flex max-h-[80vh] w-full max-w-md flex-col rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center justify-between">
          <h2 id={titoloId} className="flex items-center gap-2 text-base font-bold text-fg">
            <Users aria-hidden className="h-4 w-4 text-brand" />
            {t('invite.pickFriend')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('invite.close')}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted active:opacity-80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {mostraRicerca && (
          <div className="relative mb-3 shrink-0">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('invite.pickFriendSearch')}
              aria-label={t('invite.pickFriendSearch')}
              className="h-10 w-full rounded-xl border border-border bg-bg pl-9 pr-3 text-sm text-fg outline-none placeholder:text-fg-muted focus:ring-1 focus:ring-brand"
            />
          </div>
        )}

        {/* `friends.data` vince su un errore di una rilettura successiva (come `lib/limulo/sfida.ts`):
            un elenco gia' letto non sparisce dietro un refetch fallito. */}
        {friends.data !== undefined ? (
          totale === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted" role="status">
              {t('invite.pickFriendEmpty')}
            </p>
          ) : elenco.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted" role="status">
              {t('invite.pickFriendNoMatch')}
            </p>
          ) : (
            <ul className="flex flex-col gap-1 overflow-y-auto">
              {elenco.map(({ amico, nome }) => {
                const isOnline = onlineIds.has(amico.friendUserId);
                return (
                  <li key={amico.friendUserId}>
                    <button
                      type="button"
                      onClick={() => {
                        haptics.selection();
                        onScegli({ userId: amico.friendUserId, nome });
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-opacity active:opacity-80"
                    >
                      <div className="relative shrink-0">
                        <UserAvatar userId={amico.friendUserId} label={nome} avatarUrl={amico.avatarUrl} size="sm" />
                        {isOnline && (
                          <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-bg-secondary p-0.5">
                            <PresenceBadge status="online" />
                          </span>
                        )}
                      </div>
                      <span data-testid="amico-nome" className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
                        {nome}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )
        ) : friends.isError ? (
          <p className="py-6 text-center text-sm text-danger" role="alert">
            {t('invite.pickFriendError')}
          </p>
        ) : (
          // Nessun dato ANCORA e nessun errore: sta caricando (o deve ancora partire). Mai
          // affermare un errore su una domanda che non ha ancora avuto risposta.
          <p className="py-6 text-center text-sm text-fg-muted" role="status">
            {t('invite.pickFriendLoading')}
          </p>
        )}

        {onPreferisciCodice !== undefined && (
          <button
            type="button"
            onClick={() => {
              haptics.selection();
              onPreferisciCodice();
            }}
            className="mt-3 shrink-0 self-center text-xs font-semibold text-fg-muted underline active:opacity-80"
          >
            {t('invite.pickFriendFallback')}
          </button>
        )}
      </div>
    </div>
  );
}
